import type { LucideIcon } from 'lucide-react';
import {
  Boxes,
  ClipboardCheck,
  ClipboardList,
  Download,
  PackagePlus,
  Send,
  ShoppingCart,
  Undo2,
} from 'lucide-react';
import type { MenuItem } from '../../../../components/Menu';

/**
 * The stock-levels SHORTCUTS, as menu entries on the Create button's trailing edge.
 *
 * <b>They were a standing STRIP of eight chips above the table until 28 Sep 2026.</b> Product
 * call: the strip spent a band of every screen on controls most readers never press, and six of
 * the eight cannot be pressed at all yet. They are a `<SplitButton>`'s menu now — one click
 * instead of none, and no width at all.
 *
 * <b>TWO of the eight have no module behind them</b>, and they are DISABLED with the reason
 * rather than hidden. Product call, 18 Sep 2026, and it is a deliberate departure from
 * hide-don't-disable: that rule is about a control a reader may never use, and these are
 * controls nobody can use YET. Naming the module each is waiting on turns a dead entry into the
 * module's own roadmap, which is what the hospital's screen communicates at a glance.
 *
 * <b>It was SIX of the eight until 30 Sep 2026</b>, and that number is worth keeping in the
 * history rather than just correcting: this exception exists to RETIRE ITSELF one entry at a
 * time, and it is most of the way there. Purchase Requests came alive on 29 Sep, and Purchase
 * Orders, Goods Receipts and Inventory Audits on 30 Sep — the last two having sat here claiming
 * their modules were unbuilt for a day after they landed, which is the one failure state this
 * pattern has: a dead control asserting something false. <b>Check these against the rail
 * whenever a Logistics module ships.</b> Returns and Export are what is left.
 *
 * <b>What the move COSTS is the glance, and it is worth saying rather than discovering.</b> The
 * exception above was argued on a strip that states the roadmap without being opened; a menu has
 * to be opened first. What survives is the half that did the work — the entries are drawn ONCE
 * for the page rather than once per row, which is the line that rule actually draws, and every
 * one of them still says what it is waiting on.
 *
 * <b>ISS came alive on 20 Sep 2026</b>, with the Issuances module — it was seven of eight until
 * then. An entry that gains a module gains the ordinary permission wording with it, exactly as
 * UNT has: what a reader without the key meets is "you do not have access", never a sentence
 * about the product being unfinished.
 *
 * <b>The ABBREVIATIONS are gone with the chips.</b> PR, PO, GR and the rest were what a chip
 * could fit, and a menu row has room for the words. Nothing else in the product quotes those
 * three letters.
 *
 * <b>The five PENDING ones name the DOCUMENT; the live ones name the ACT.</b> Product call,
 * 28 Sep 2026 — they read "New Purchase Request" for a day and the verb was doing nothing:
 * nothing can be created there, so "New" promised an act the entry cannot perform and cost the
 * width two of them were already truncating in. `New Issuance` keeps its verb, because that
 * one opens a form.
 *
 * <b>It is a BUILDER rather than a component</b>, because what it produces is data the shell
 * renders: `<IndexPage shortcutMenu>` takes `MenuItem[]`, and a component returning chips could
 * not be hung off a button the shell owns. `navigate` is passed in for the same reason — a
 * builder is not a hook and must not call one.
 */
export function stockLevelShortcutItems({
  canReadUnits,
  canIssue,
  onIssue,
  navigate,
}: {
  canReadUnits: boolean;
  canIssue: boolean;
  /** Opens the Issue form. The PAGE owns the modal — a menu entry does not hold one. */
  onIssue: () => void;
  navigate: (to: string) => void;
}): MenuItem[] {
  const pending = (module: string) => `${module} is not built yet, so there is nothing to open.`;
  const glyph = (Icon: LucideIcon) => <Icon className="h-4 w-4" aria-hidden="true" />;

  return [
    /*
      LIVE from 29 Sep 2026, and it is this exception EXPIRING as it was written to: "a chip
      gains a module and drops out of this exception, one at a time". Purchase Requests exists
      under Procurement › Purchasing, so an entry still saying Procurement is not built would
      be a dead control asserting something false.

      It NAVIGATES rather than opening the form, because a request is raised against a register
      the reader has to be standing in — and it carries the key of what it OPENS, never this
      page's own.
    */
    {
      key: 'pr',
      label: 'Purchase Request',
      icon: glyph(ClipboardList),
      permission: 'purchase-requests.read',
      onClick: () => navigate('/procurement/purchasing/purchase-requests'),
    },
    /*
      LIVE from 29 Sep 2026, the same day Purchase Orders landed. It was still saying "Procurement
      is not built yet", which had stopped being true — a dead control asserting something false
      is the one thing this exception must never leave behind.
    */
    {
      key: 'po',
      label: 'Purchase Order',
      icon: glyph(ShoppingCart),
      permission: 'purchase-orders.read',
      onClick: () => navigate('/procurement/purchasing/purchase-orders'),
    },
    /*
      LIVE from 30 Sep 2026 — this module. It NAVIGATES rather than opening the wizard, because a
      delivery is recorded against an order the reader picks in step one, and the register is
      where that starts. The key is the one it OPENS, never this page's own.
    */
    {
      key: 'gr',
      label: 'Goods Receipt',
      icon: glyph(PackagePlus),
      permission: 'goods-receipts.read',
      onClick: () => navigate('/inventory/goods-receipts'),
    },
    {
      key: 'rts',
      label: 'Return to Supplier',
      icon: glyph(Undo2),
      disabledReason: pending('Returns'),
      onClick: () => undefined,
    },
    /*
      LIVE from 30 Sep 2026. Inventory Audits landed on 29 Sep and this chip was still saying
      stock counts were unbuilt — the same stale claim the Purchase Order one above carried.
    */
    {
      key: 'aud',
      label: 'Inventory Audit',
      icon: glyph(ClipboardCheck),
      permission: 'inventory-audits.read',
      onClick: () => navigate('/inventory/inventory-audits'),
    },
    {
      /*
        LIVE from 20 Sep 2026. It opens the Issue form with nothing seeded — the menu is over
        the whole list rather than over a row — which is what separates it from the row kebab's
        own Issue, where the reader has already named the item.

        `issuances.create` rather than `read`, because it WRITES: a reader who may look at the
        register is not thereby somebody who may release property against a signature.
      */
      key: 'iss',
      label: 'New Issuance',
      icon: glyph(Send),
      divider: true,
      disabledReason: canIssue ? null : 'You do not have access to Issuances.',
      onClick: onIssue,
    },
    {
      // The other one that works. Its key is the module's own, so a reader without it does not
      // meet an entry that 403s.
      key: 'unt',
      label: 'View Stock Units',
      icon: glyph(Boxes),
      disabledReason: canReadUnits ? null : 'You do not have access to Stock Units.',
      onClick: () => navigate('/inventory/stock-units'),
    },
    {
      key: 'dwl',
      label: 'Export this view',
      icon: glyph(Download),
      disabledReason: pending('Export'),
      onClick: () => undefined,
    },
  ];
}
